"use client"

import { useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { DashboardMockup } from "./dashboard-mockup"
import { QuickStartSection } from "./quick-start-section"
import useSWR from "swr"
import { Sparkles } from "lucide-react"
import { EarlyAccessDialog } from "./early-access-dialog"

const fetcher = (url: string) => fetch(url).then(res => res.json())

export function HeroSection() {
  const [showEarlyAccess, setShowEarlyAccess] = useState(false)
  const { data, mutate } = useSWR("/api/early-access/count", fetcher, { revalidateOnFocus: true, refreshInterval: 0 })
  const signupCount = data?.count ?? 0
  const percent = Math.min(Math.round((signupCount / 1000) * 100), 100) // Assuming 1000 is the launch goal

  return (
    <>
      <section className="py-10 lg:py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 mb-4">
                  🚀 Coming Soon - Join Early Access
                </div>
                <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Track revenue from <span className="text-green-600">all your channels</span> in one place
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  revvu helps creators automatically monitor earnings across multiple platforms or input them manually.
                  Get insights into your revenue streams and grow your business.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg"
                  onClick={() => setShowEarlyAccess(true)}
                >
                  Join Early Access
                </Button>
                <Button asChild variant="outline" size="lg" className="px-8 py-4 text-lg">
                  <Link href="/pricing">View Pricing</Link>
                </Button>
              </div>

              {/* Early Access Perks Card */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 my-4">
                <div className="flex items-center gap-2 text-green-800 text-sm font-medium mb-1">
                  <Sparkles className="h-4 w-4" />
                  Early Access Perks
                </div>
                <ul className="text-sm text-green-700 space-y-1 ml-6">
                  <li>• 30-day free trial (instead of 14 days)</li>
                  <li>• 20% off your first year</li>
                  <li>• Priority onboarding support</li>
                </ul>
              </div>

              {/* Trust indicators */}
              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>No setup fees</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>Cancel anytime</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>Free plan available</span>
                </div>
              </div>

              {/* Early access stats */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">Early access signups</div>
                  <div className="text-2xl font-bold text-green-600">{signupCount}</div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: `${percent}%` }}></div>
                </div>
                <div className="text-xs text-gray-500 mt-1">{percent}% to launch goal</div>
              </div>
            </div>

            {/* Right side - Dashboard Mockup */}
            <div className="lg:pl-8">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Start Section */}
      <QuickStartSection />

      <EarlyAccessDialog open={showEarlyAccess} onOpenChange={setShowEarlyAccess} />
    </>
  )
}
